import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDataChannel, useLocalParticipant, useRoomContext } from '@livekit/components-react'
import type { ReceivedDataMessage } from '@livekit/components-core'
import {
  ConnectionState,
  RoomEvent,
  Track,
  type LocalTrackPublication,
  type Participant,
  type RemoteParticipant,
  type TrackPublication,
} from 'livekit-client'

import { ACCESS_FRESH_MS, type MicAccess } from './deviceProbe'
import { detectCanScreenShare, type ShareFailure } from './screenShare'
import { isTeacher as isTeacherIdentity } from './identity.ts'

/**
 * The lightweight social layer of a class: reactions, celebrations, raised hands.
 *
 * Two transports, chosen deliberately, because getting this wrong produces bugs
 * that only appear for whoever joined last:
 *
 * - Reactions and celebrations are EVENTS. They happen once and are gone, so they
 *   go over the data channel as lossy messages. A dropped 👍 costs nothing, and
 *   staying realtime matters more than guaranteed delivery.
 * - A raised hand is STATE. It must still be raised for someone who joins thirty
 *   seconds later, so it lives in participant attributes, which LiveKit
 *   replicates to everyone including late joiners.
 *
 * Authorization note: `participant.identity` comes from the token the API mints
 * (`Student:168684`, `Tutor:9431`), not from anything the client asserts. So
 * teacher-only actions can be gated on the identity prefix and a student cannot
 * forge one.
 */

export const REACTIONS = ['👍', '👏', '❤️', '😂', '🎉', '🤔'] as const
export type Reaction = (typeof REACTIONS)[number]

const TOPIC = 'ischool-classroom'
const HAND_ATTRIBUTE = 'hand_raised_at'
/**
 * Advertised once on join (and re-asserted after a hard reconnect): whether
 * this device could open a screen picker at all. The tutor's "ask to share"
 * reads it, so a child on an Android phone is never asked to do something the
 * platform cannot do. Attribute rather than a data-channel announce because
 * the server replays attributes to late joiners — a tutor who joins after the
 * class still sees who can share.
 */
const CAN_SHARE_ATTRIBUTE = 'can_share'
/**
 * Whether the browser can give this participant a live microphone at all:
 * `ok | blocked | missing | busy | unknown`. Same reasoning as CAN_SHARE — an
 * attribute rather than a data message, because the tutor who needs it may join
 * AFTER the child whose microphone is broken, and attributes are replayed.
 *
 * This exists because `isMicrophoneEnabled === false` conflates three different
 * worlds — chose to mute, browser blocking, no microphone — and on 2026-08-02 a
 * tutor spent nineteen minutes asking a child to unmute a microphone the browser
 * was never going to hand over. Kept deliberately COARSE: tutors need three
 * coachable buckets, not a forensic report, and over-specific claims ("Android
 * app permission") would be wrong often enough to teach them to distrust it.
 *
 * Only the TUTOR's UI renders this for other people. Attributes are readable by
 * everyone in the room, so the privacy line that matters is who draws it — a
 * child's device trouble is not another child's business.
 */
const MIC_ACCESS_ATTRIBUTE = 'mic_access'
/** Same contract as MIC_ACCESS_ATTRIBUTE, for the camera. */
const CAM_ACCESS_ATTRIBUTE = 'cam_access'
/**
 * When the two above were measured, epoch ms. A device state is a claim about a
 * moment — a headset gets unplugged, a parent flips a permission — so a verdict
 * old enough to be doubtful is presented as `unknown` rather than as fact. A
 * tutor talked into switching provider by a stale "blocked" is worse off than one
 * told plainly that we do not know.
 */
const ACCESS_AT_ATTRIBUTE = 'device_access_at'
/**
 * Anything outside this set is treated as absent — attributes are client-set.
 *
 * Keep in lockstep with `DeviceAccess` in deviceProbe.ts. TypeScript will NOT
 * catch a member missing from this array (a short `MicAccess[]` is still a valid
 * `MicAccess[]`), and the failure is silent in the worst way: the publishing
 * client sets the attribute, the receiving tutor's client drops it on the floor,
 * and the roster shows a plain mute icon for a fault we had correctly detected.
 * That is precisely the 2026-08-02 failure mode, reintroduced by omission.
 */
const MIC_ACCESS_VALUES: MicAccess[] = ['ok', 'blocked', 'missing', 'busy', 'silent', 'unknown']

interface ReactionEvent {
  kind: 'reaction'
  emoji: Reaction
}

interface CelebrationEvent {
  kind: 'celebration'
}

/**
 * A teacher asking one student to turn something back on.
 *
 * This is a REQUEST, not a command, and that is the whole point. The server can
 * mute a child (Meetings::Moderation) but never unmute one, so coming back is
 * always the student's own tap. It travels on the data channel rather than
 * through our API because nothing needs to be enforced — the student decides.
 */
interface UnmuteRequestEvent {
  kind: 'unmute_request'
  target: string
  source: 'microphone' | 'camera'
}

/** Teacher asks for a device walkthrough. Never enables a track. */
interface MicHelpRequestEvent {
  kind: 'mic_help_request'
  target: string
  device?: 'mic' | 'camera'
}

/**
 * "Would you show us your screen?" — the same REQUEST shape as unmute, and for
 * the same reason: the browser will not open a screen picker without the
 * child's own tap, and we would not want it to if it could.
 */
interface ShareRequestEvent {
  kind: 'share_request'
  target: string
  requestId: string
}

/** The student's answer, addressed back to the tutor who asked. */
interface ShareResultEvent {
  kind: 'share_result'
  target: string
  requestId: string
  result: ShareAnswer
}

export type ShareAnswer = 'accepted' | 'declined' | 'timeout' | 'busy' | ShareFailure

type Signal =
  | ReactionEvent
  | CelebrationEvent
  | UnmuteRequestEvent
  | MicHelpRequestEvent
  | ShareRequestEvent
  | ShareResultEvent

export type UnmuteRequest = { source: 'microphone' | 'camera'; id: number }
export type ShareRequest = { id: number; requestId: string; from: string }
/** What the tutor sees for one student after asking. */
export type SharePending =
  | { state: 'waiting'; requestId: string }
  | { state: 'answered'; answer: ShareAnswer; requestId: string }

const SHARE_ANSWERS = new Set<string>([
  'accepted', 'declined', 'timeout', 'busy',
  'cancelled', 'unfocused', 'unsupported', 'blocked', 'failed',
])
/** A child gets this long to answer before the ask quietly expires. */
export const SHARE_REQUEST_TTL_MS = 30_000

/**
 * A reaction in flight, with its own randomised motion.
 *
 * Every value here is rolled per reaction so two 👍 sent a second apart do not
 * play the identical animation — which is what made a busy class look like a
 * repeating loop rather than a room full of people.
 */
export interface FloatingReaction {
  id: string
  emoji: Reaction
  from: string
  /** Horizontal jitter so simultaneous reactions do not stack into one column. */
  offset: number
  /** Peak scale of the initial pop. */
  peak: number
  /** How far up the tile it travels, as a percentage of tile height. */
  rise: number
  /** Total flight time in seconds. */
  duration: number
  /** Rotation at rest, degrees — a little tilt reads as hand-thrown. */
  spin: number
  /** Sideways wobble amplitude, in px, applied mid-flight. */
  sway: number
}

/** Inclusive random float, rounded so the CSS variable stays short. */
function between(min: number, max: number, decimals = 2): number {
  const value = min + Math.random() * (max - min)
  return Number(value.toFixed(decimals))
}

/**
 * Shallow record equality used by `readHands` (B1, meet-opt-03) to bail a
 * setState when nothing actually changed. New maps are a fresh object every
 * call, so `===` would never match; this checks key-set + per-key value.
 * Bails on the common "attribute event for one peer, my map already reflects
 * it" path so an attribute storm during a 25-participant join wave stops
 * re-rendering the entire Classroom tree per event.
 */
function sameRecord<T extends string | number | boolean>(
  a: Record<string, T>,
  b: Record<string, T>,
): boolean {
  const ak = Object.keys(a)
  if (ak.length !== Object.keys(b).length) return false
  for (const k of ak) {
    if (a[k] !== b[k]) return false
  }
  return true
}

export function isTeacher(identity: string | undefined): boolean {
  return isTeacherIdentity(identity)
}

export function isParent(identity: string | undefined): boolean {
  if (!identity) return false
  // Same trust model as isTeacher: the `Parent:` prefix is minted into the
  // signed token by the API, never chosen by the client. A parent joins as a
  // plain Participant, so this is the only way to tell them apart from a student.
  return identity.startsWith('Parent:')
}

export function useClassroomSignals() {
  const room = useRoomContext()
  const { localParticipant } = useLocalParticipant()
  const [floating, setFloating] = useState<FloatingReaction[]>([])
  const [celebrating, setCelebrating] = useState(false)
  const [hands, setHands] = useState<Record<string, number>>({})
  const [unmuteRequest, setUnmuteRequest] = useState<UnmuteRequest | null>(null)
  const [micHelpRequest, setMicHelpRequest] = useState<{ id: number; device: 'mic' | 'camera' } | null>(null)
  const [shareRequest, setShareRequest] = useState<ShareRequest | null>(null)
  /** Tutor side: identity → waiting / answered. */
  const [sharePending, setSharePending] = useState<Record<string, SharePending>>({})
  /** Identities whose device advertised that it can capture a screen. */
  const [canShare, setCanShare] = useState<Record<string, boolean>>({})
  const [micAccess, setMicAccess] = useState<Record<string, MicAccess>>({})
  const [camAccess, setCamAccess] = useState<Record<string, MicAccess>>({})
  const camAccessRef = useRef<MicAccess | null>(null)
  /** Our own last-known access, so a reconnect can re-assert it. */
  const micAccessRef = useRef<MicAccess | null>(null)
  /** The live ask's expiry timer, and whether an Accept has taken it over. */
  const shareTtlRef = useRef<number | undefined>(undefined)
  const shareTtlHeldRef = useRef(false)
  /** My own raised-hand timestamp, for re-asserting after a hard reconnect. */
  const handRaisedAtRef = useRef(0)

  // Every pending reaction/celebration timer, so leaving the room does not leave
  // setState calls scheduled against an unmounted tree.
  const timersRef = useRef<Set<number>>(new Set())
  const later = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      timersRef.current.delete(id)
      fn()
    }, ms)
    timersRef.current.add(id)
  }, [])
  useEffect(
    () => () => {
      for (const id of timersRef.current) window.clearTimeout(id)
      timersRef.current.clear()
    },
    [],
  )

  const decoder = useMemo(() => new TextDecoder(), [])
  const encoder = useMemo(() => new TextEncoder(), [])

  const showReaction = useCallback((emoji: Reaction, from: string) => {
    const id = `${from}-${emoji}-${performance.now()}`
    const duration = between(2.4, 4)
    setFloating((current) => [
      // Capped: twenty simultaneous reactions must not stall a low-end phone.
      ...current.slice(-11),
      {
        id,
        emoji,
        from,
        offset: between(-34, 34, 0),
        peak: between(1.15, 1.8),
        rise: between(42, 72, 0),
        duration,
        spin: between(-24, 24, 0),
        sway: between(-22, 22, 0),
      },
    ])
    // Cleared on its OWN duration rather than a shared constant, so a slow
    // reaction is not cut off and a fast one does not linger invisibly.
    later(() => setFloating((current) => current.filter((r) => r.id !== id)), duration * 1000 + 200)
  }, [later])

  // The data-channel handler is kept STABLE: @livekit/components-react
  // memoises the room's DataReceived subscription on the handler identity, so
  // a fresh inline arrow per render tears that listener off and re-adds it after
  // every Classroom re-render (every reaction, hand raise, attribute event) —
  // churn on the device least able to afford it, and a brief window in which a
  // reliable signal (an unmute or share REQUEST) is not delivered to this
  // client at all. The body reads live values like localParticipant.identity, so
  // it is dispatched through a ref that is reassigned every render WITHOUT ever
  // changing the callback identity, keeping the subscription permanent.
  const onMessageRef = useRef<(message: ReceivedDataMessage<typeof TOPIC>) => void>(
    () => {},
  )
  const onMessage = useCallback(
    (message: ReceivedDataMessage<typeof TOPIC>) => onMessageRef.current(message),
    [],
  )
  const { send } = useDataChannel(TOPIC, onMessage)
  onMessageRef.current = (message) => {
    let signal: Signal
    try {
      const parsed: unknown = JSON.parse(decoder.decode(message.payload))
      // JSON.parse happily returns null, numbers and arrays — all valid JSON,
      // none of them signals, and `null.kind` THREW past this catch and took
      // the receive handler down for the lesson. Same bug class the chat
      // handler was hardened against; this handler never got the same fix.
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return
      if (typeof (parsed as { kind?: unknown }).kind !== 'string') return
      signal = parsed as Signal
    } catch {
      return // malformed payloads are ignored rather than crashing the class
    }

    const from = message.from?.identity ?? 'unknown'

    if (signal.kind === 'reaction' && REACTIONS.includes(signal.emoji)) {
      showReaction(signal.emoji, from)
      return
    }

    // Celebrations are a teacher's to give. Checked on RECEIPT as well as on
    // send, so a hand-crafted message from a student's console does nothing.
    if (signal.kind === 'celebration' && isTeacher(from)) {
      setCelebrating(true)
      later(() => setCelebrating(false), 4_500)
      return
    }

    // Same receipt-side check, and additionally addressed to me: a classmate
    // cannot make another child's screen ask them to switch a camera on.
    if (
      signal.kind === 'unmute_request' &&
      isTeacher(from) &&
      signal.target === localParticipant?.identity &&
      (signal.source === 'microphone' || signal.source === 'camera')
    ) {
      // The id makes a second request re-open a prompt the student dismissed.
      setUnmuteRequest({ source: signal.source, id: performance.now() })
      return
    }

    if (
      signal.kind === 'mic_help_request' &&
      isTeacher(from) &&
      signal.target === localParticipant?.identity
    ) {
      if (signal.device !== undefined && signal.device !== 'mic' && signal.device !== 'camera') return
      setMicHelpRequest({ id: performance.now(), device: signal.device ?? 'mic' })
      return
    }

    // Ask-to-share: teacher-sent, addressed to me, and never auto-accepted.
    if (
      signal.kind === 'share_request' &&
      isTeacher(from) &&
      signal.target === localParticipant?.identity &&
      typeof signal.requestId === 'string' &&
      signal.requestId.length <= 64
    ) {
      setShareRequest({ id: performance.now(), requestId: signal.requestId, from })
      return
    }

    // The answer, which only the tutor who asked ever sees.
    if (
      signal.kind === 'share_result' &&
      signal.target === localParticipant?.identity &&
      typeof signal.result === 'string' &&
      SHARE_ANSWERS.has(signal.result)
    ) {
      // LATEST WINS for a matching requestId, whatever state the entry is in.
      // Gating on state === 'waiting' looked safer but broke the two honest
      // paths the prompt design creates: a retriable failure answers first and
      // the child's successful RETRY (same requestId) was then dropped; and a
      // child who held the picker open past the tutor's 30s local timeout had
      // their late 'accepted' ignored. Unsolicited packets still die here —
      // no entry, or a stale requestId, changes nothing.
      setSharePending((current) => {
        const entry = current[from]
        if (!entry || entry.requestId !== signal.requestId) return current
        return {
          ...current,
          [from]: { state: 'answered', answer: signal.result, requestId: signal.requestId },
        }
      })
      // Clearly-final outcomes flash and clear — a decline must not follow a
      // child around. Timeouts and retriable failures STAY until superseded:
      // clearing them would delete the entry a late accept needs to overwrite.
      if (signal.result === 'accepted' || signal.result === 'declined' || signal.result === 'cancelled') {
        // Scoped to THIS ask: an unscoped clear deleted whatever entry existed,
        // so declining ask A and re-asking B within six seconds erased B's
        // waiting entry — and B's answer then died as unsolicited.
        later(() => clearSharePendingRef.current(from, signal.requestId), 6_000)
      }
    }
  }

  /**
   * Publish one classroom signal. **Never rejects** — resolves `false` instead.
   *
   * Same contract, and same reason, as `publish` in surface/useSurface.ts: `send`
   * resolves to `publishData(): Promise<void>`, so `void send(...)` inside a
   * synchronous `try/catch` caught only a synchronous encode throw and turned
   * every real transport failure into an unhandled rejection. A dropped reaction
   * genuinely does not matter; a dropped unmute REQUEST does, and that one is
   * reliable precisely because the sender expects a result.
   *
   * No failure UI here on purpose — resolving `false` just makes the outcome
   * available to a caller that wants it, instead of losing it to the event loop.
   */
  const publish = useCallback(
    (signal: Signal, options?: { reliable?: boolean; to?: string[] }): Promise<boolean> => {
      let bytes: Uint8Array
      try {
        bytes = encoder.encode(JSON.stringify(signal))
      } catch {
        /* a dropped reaction is not worth surfacing to a child mid-lesson */
        return Promise.resolve(false)
      }
      return send(bytes, {
        // Reactions default to lossy — they are ephemeral, and realtime beats
        // retransmission. Anything the sender expects a RESULT from must be
        // reliable instead.
        reliable: options?.reliable ?? false,
        destinationIdentities: options?.to,
      }).then(
        () => true,
        () => false,
      )
    },
    [send, encoder],
  )

  const sendReaction = useCallback(
    (emoji: Reaction) => {
      // Rendered locally FIRST. Data packets are forwarded to other participants,
      // so there is no echo back to the sender — without this, tapping an emoji
      // closed the picker and produced nothing visible.
      showReaction(emoji, localParticipant?.identity ?? 'me')
      publish({ kind: 'reaction', emoji })
    },
    [publish, showReaction, localParticipant],
  )

  const askToUnmute = useCallback(
    (identity: string, source: 'microphone' | 'camera') => {
      // Reliable and addressed to that student alone: a dropped request would
      // leave the teacher believing they had asked, and no classmate needs to
      // see it.
      publish({ kind: 'unmute_request', target: identity, source }, { reliable: true, to: [identity] })
    },
    [publish],
  )

  const dismissUnmuteRequest = useCallback(() => setUnmuteRequest(null), [])

  const askStudentMicHelp = useCallback(
    (identity: string, device: 'mic' | 'camera' = 'mic') => {
      publish({ kind: 'mic_help_request', target: identity, device }, { reliable: true, to: [identity] })
    },
    [publish],
  )
  const dismissMicHelpRequest = useCallback(() => setMicHelpRequest(null), [])

  // ---- ask to share ----------------------------------------------------------
  const askToShare = useCallback(
    (identity: string) => {
      const requestId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
      publish({ kind: 'share_request', target: identity, requestId }, { reliable: true, to: [identity] })
      setSharePending((current) => ({ ...current, [identity]: { state: 'waiting', requestId } }))
      // Local expiry so "waiting…" cannot run forever — bound to THIS ask's
      // requestId (a re-ask must not be timed out by its predecessor's timer),
      // and the entry KEEPS the requestId: a child who tapped Accept holds
      // their own prompt open past 30s while choosing a window, and their late
      // 'accepted' must still be able to overwrite this timeout (latest-wins
      // in the receive path).
      later(() => {
        setSharePending((current) => {
          const entry = current[identity]
          if (!entry || entry.state !== 'waiting' || entry.requestId !== requestId) return current
          return { ...current, [identity]: { state: 'answered', answer: 'timeout', requestId } }
        })
      }, SHARE_REQUEST_TTL_MS)
    },
    [publish, later],
  )

  /**
   * The student's answer, addressed to the tutor who asked — nobody else.
   *
   * The tutor is told IMMEDIATELY (they should never wait on a child reading an
   * error), but the prompt only closes on a TERMINAL answer. A retriable
   * failure — the tab was not focused, the OS refused — leaves the prompt up
   * with its Try again button, which used to unmount the instant the result
   * was published and left the child with a flash and no way back.
   */
  const answerShare = useCallback(
    (request: ShareRequest, result: ShareAnswer) => {
      publish(
        { kind: 'share_result', target: request.from, requestId: request.requestId, result },
        { reliable: true, to: [request.from] },
      )
      const terminal = result === 'accepted' || result === 'declined' || result === 'cancelled' || result === 'timeout'
      if (terminal) {
        setShareRequest((current) => (current?.requestId === request.requestId ? null : current))
      }
    },
    [publish],
  )

  /**
   * Called SYNCHRONOUSLY from the student's Accept tap, before the screen
   * picker opens: the picker is modal and a child choosing a window can easily
   * take longer than the ask's lifetime, and a timeout firing behind it told
   * the tutor "no answer" moments before the share actually arrived. Clearing a
   * timer costs no await, so the gesture survives.
   */
  const holdShareTimeout = useCallback(() => {
    shareTtlHeldRef.current = true
    window.clearTimeout(shareTtlRef.current)
  }, [])

  /** Scoped: request B can replace A while A's accept is mid-picker, and A's
   *  completion must not tear B down. No id = explicit user dismissal of
   *  whatever is showing. */
  const dismissShareRequest = useCallback((requestId?: string) => {
    setShareRequest((current) =>
      !requestId || current?.requestId === requestId ? null : current,
    )
  }, [])

  /** Latest-ref so the receive handler can clear without a dependency cycle. */
  const clearSharePendingRef = useRef<(identity: string, requestId?: string) => void>(() => {})

  /** Tutor: clear one student's status flash back to idle. When a requestId
   *  is given, only that ask's entry is cleared — a stale timer must never
   *  delete its successor. */
  const clearSharePending = useCallback((identity: string, requestId?: string) => {
    setSharePending((current) => {
      const entry = current[identity]
      if (!entry) return current
      if (requestId && entry.requestId !== requestId) return current
      const next = { ...current }
      delete next[identity]
      return next
    })
  }, [])
  clearSharePendingRef.current = clearSharePending

  // The child's prompt expires on its own: an ask nobody answered must not sit
  // on screen for the rest of the lesson.
  useEffect(() => {
    if (!shareRequest) return
    shareTtlHeldRef.current = false
    shareTtlRef.current = window.setTimeout(() => {
      // Held means the child is mid-picker — their answer is coming.
      if (shareTtlHeldRef.current) return
      // Timeout is reported so the tutor stops waiting; the child sees nothing.
      answerShare(shareRequest, 'timeout')
    }, SHARE_REQUEST_TTL_MS)
    return () => window.clearTimeout(shareTtlRef.current)
  }, [shareRequest, answerShare])

  const celebrate = useCallback(() => {
    // Same reasoning: the teacher who pressed it must see it happen.
    setCelebrating(true)
    later(() => setCelebrating(false), 4_500)
    publish({ kind: 'celebration' })
  }, [publish, later])

  // ---- Raised hands: state, via participant attributes ---------------------

  const readHands = useCallback(() => {
    if (!room) return
    const next: Record<string, number> = {}
    const consider = (identity: string, attributes: Record<string, string> | undefined) => {
      const raw = attributes?.[HAND_ATTRIBUTE]
      const at = raw ? Number(raw) : 0
      if (at > 0) next[identity] = at
    }
    consider(room.localParticipant.identity, room.localParticipant.attributes)
    room.remoteParticipants.forEach((p: RemoteParticipant) => consider(p.identity, p.attributes))
    // B1 (meet-opt-03) — equality-bail per map. The old code called
    // setHands/setCanShare/setMicAccess/setCamAccess unconditionally on every
    // ParticipantAttributesChanged/ParticipantConnected tick. A 25-participant
    // join wave publishes several attribute events each, so 4 setStates
    // re-render the entire Classroom tree per event even when nothing relevant
    // changed (e.g., a hand raise updates `hands` but `canShare`/`mics`/`cams`
    // are unchanged). Bail with reference equality: the new map is a fresh
    // object every call, so == would never match. Shallow compare keys + values.
    // Same pattern as useDataSaver:330-341 (readSaverAttributes).
    setHands((prev) => (sameRecord(prev, next) ? prev : next))
    // Who can be asked to share. MISSING means no, deliberately: a student
    // whose attributes have not landed yet must not be asked to do something
    // we have no evidence they can do.
    const sharers: Record<string, boolean> = {}
    room.remoteParticipants.forEach((p: RemoteParticipant) => {
      if (p.attributes?.[CAN_SHARE_ATTRIBUTE] === '1') sharers[p.identity] = true
    })
    setCanShare((prev) => (sameRecord(prev, sharers) ? prev : sharers))
    // Microphone access per participant. A missing value stays absent rather than
    // defaulting to 'ok': claiming a microphone works when we have no evidence is
    // how the tutor ends up asking a blocked child to unmute.
    const mics: Record<string, MicAccess> = {}
    const cams: Record<string, MicAccess> = {}
    const now = Date.now()
    room.remoteParticipants.forEach((p: RemoteParticipant) => {
      // Stale verdicts are dropped rather than shown: see ACCESS_AT_ATTRIBUTE.
      const at = Number(p.attributes?.[ACCESS_AT_ATTRIBUTE] ?? 0)
      if (at > 0 && now - at > ACCESS_FRESH_MS) return
      const mic = p.attributes?.[MIC_ACCESS_ATTRIBUTE]
      if (mic && MIC_ACCESS_VALUES.includes(mic as MicAccess)) mics[p.identity] = mic as MicAccess
      const cam = p.attributes?.[CAM_ACCESS_ATTRIBUTE]
      if (cam && MIC_ACCESS_VALUES.includes(cam as MicAccess)) cams[p.identity] = cam as MicAccess
    })
    setMicAccess((prev) => (sameRecord(prev, mics) ? prev : mics))
    setCamAccess((prev) => (sameRecord(prev, cams) ? prev : cams))
  }, [room])

  // Advertise our own capability once per connection. Re-asserted on a full
  // reconnect, which can hand back a participant without its attributes.
  useEffect(() => {
    if (!room) return
    const advertise = () => {
      // A hard reconnect creates a fresh participant, so EVERY app attribute
      // must be re-published, not just this one — a raised hand vanishing
      // because the network blipped is a child losing their turn silently.
      const attributes: Record<string, string> = {
        [CAN_SHARE_ATTRIBUTE]: detectCanScreenShare() ? '1' : '0',
      }
      const hand = handRaisedAtRef.current
      if (hand) attributes[HAND_ATTRIBUTE] = String(hand)
      // Carried over from the prejoin probe, and refreshed by publishMicAccess
      // whenever it changes. Only advertised once we actually know something:
      // publishing 'unknown' on every join would drown the signal that matters.
      const mic = micAccessRef.current
      const cam = camAccessRef.current
      if (mic) attributes[MIC_ACCESS_ATTRIBUTE] = mic
      if (cam) attributes[CAM_ACCESS_ATTRIBUTE] = cam
      if (mic || cam) attributes[ACCESS_AT_ATTRIBUTE] = String(Date.now())
      room.localParticipant
        .setAttributes(attributes)
        .catch(() => undefined /* advisory: the Share button still self-checks */)
    }
    // Only once CONNECTED, and this is load-bearing rather than tidy. The room
    // object exists as soon as <LiveKitRoom> renders, so this effect runs while
    // the connection is still being established — and livekit-client's signal
    // layer DISCARDS a request sent that early without raising anything ("skipping
    // signal request … SignalClient disconnected", whose own comment notes it
    // happens "in the mist of room.connect() initializing"). So the advertise
    // silently never left the browser, `can_share` never landed, and because
    // MISSING deliberately means "no", every tutor was told a perfectly capable
    // desktop student's "device can't share a screen". The only retry was
    // Reconnected, which a healthy lesson never fires.
    if (room.state === ConnectionState.Connected) advertise()
    room.on(RoomEvent.Connected, advertise)
    room.on(RoomEvent.Reconnected, advertise)
    return () => {
      room.off(RoomEvent.Connected, advertise)
      room.off(RoomEvent.Reconnected, advertise)
    }
  }, [room])

  /**
   * A tutor stopped MY screen share — so actually stop it.
   *
   * Moderation can only ever MUTE (LiveKit has no server-side unpublish for
   * someone else's track), and livekit-client applies that mute to a share
   * publication as-is. The result was a share that had been "stopped" but was
   * still published, still muted, and still capturing: the child's browser kept
   * showing its sharing indicator, their screen was still being read by the
   * browser, and nobody had told them anything.
   *
   * This finishes the job the tutor asked for, using the same call the student's
   * own stop button uses — which unpublishes the video AND its audio companion,
   * and releases the capture so the browser indicator goes away.
   *
   * Scoped hard to the local participant's ScreenShare. Not the microphone or
   * camera, where a mute genuinely means muted and the device should stay open;
   * and not ScreenShareAudio, because killing an entire share over its sound
   * being muted would be a surprise the tutor did not ask for.
   */
  const [shareStoppedAt, setShareStoppedAt] = useState(0)
  useEffect(() => {
    if (!room) return
    const onMuted = (publication: TrackPublication, participant: Participant) => {
      if (!participant.isLocal) return
      if (publication.source !== Track.Source.ScreenShare) return
      setShareStoppedAt(Date.now())
      // The student's OWN stop unpublishes without ever muting, so this cannot
      // fire for it — there is no path here that fights the person sharing.
      // Moderation is the only thing in this app that mutes a local share (the
      // one other `mute()` call lives in mediaRecovery and is scoped to the
      // microphone and camera), which is what makes the copy safe to attribute.
      void room.localParticipant.setScreenShareEnabled(false).catch(() => undefined)
    }
    // Sharing again retires the note early. Without this, a student who was
    // stopped and immediately started a new share would be reading "your screen
    // sharing has stopped" over the top of a share that is plainly running.
    const onPublished = (publication: LocalTrackPublication) => {
      if (publication.source !== Track.Source.ScreenShare) return
      setShareStoppedAt(0)
    }
    room.on(RoomEvent.TrackMuted, onMuted)
    room.on(RoomEvent.LocalTrackPublished, onPublished)
    return () => {
      room.off(RoomEvent.TrackMuted, onMuted)
      room.off(RoomEvent.LocalTrackPublished, onPublished)
    }
  }, [room])

  useEffect(() => {
    if (!room) return
    readHands()
    // A student who leaves takes their ask status with them: "waiting for an
    // answer…" about someone no longer in the class is just confusing.
    const onGone = (participant: RemoteParticipant) => {
      readHands()
      setSharePending((current) => {
        if (!current[participant.identity]) return current
        const next = { ...current }
        delete next[participant.identity]
        return next
      })
    }
    room.on(RoomEvent.ParticipantAttributesChanged, readHands)
    room.on(RoomEvent.ParticipantConnected, readHands)
    room.on(RoomEvent.ParticipantDisconnected, onGone)
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, readHands)
      room.off(RoomEvent.ParticipantConnected, readHands)
      room.off(RoomEvent.ParticipantDisconnected, onGone)
    }
  }, [room, readHands])

  const myHandRaised = Boolean(localParticipant && hands[localParticipant.identity])
  // Mirrored for the reconnect re-advertise: a fresh participant has no
  // attributes, and the raise must survive the blip.
  handRaisedAtRef.current = localParticipant ? hands[localParticipant.identity] ?? 0 : 0

  const toggleHand = useCallback(async () => {
    if (!localParticipant) return
    try {
      await localParticipant.setAttributes({
        // Timestamped rather than boolean so a teacher can see who raised first —
        // the difference between "someone has a question" and a fair queue.
        [HAND_ATTRIBUTE]: myHandRaised ? '' : String(Date.now()),
      })
    } catch {
      /* ignore: the hand simply does not change */
    }
  }, [localParticipant, myHandRaised])

  const lowerHand = useCallback(
    async (identity: string) => {
      if (!room) return
      if (identity === room.localParticipant.identity) {
        await room.localParticipant.setAttributes({ [HAND_ATTRIBUTE]: '' })
      }
      // Lowering someone ELSE's hand needs server authority (the participant owns
      // its own attributes), so it is intentionally not attempted from here.
    },
    [room],
  )

  /** Identities with a hand up, oldest first — a fair answering order. */
  const handQueue = useMemo(
    () =>
      Object.entries(hands)
        .sort(([, a], [, b]) => a - b)
        .map(([identity]) => identity),
    [hands],
  )

  /**
   * Tell the room what our microphone can actually do. Called from the prejoin
   * handoff and whenever a retry changes the answer.
   *
   * Advisory by design: a failure to advertise must never break the class, and
   * the tutor simply falls back to today's behaviour of not knowing.
   */
  const publishDeviceAccess = useCallback(
    (mic: MicAccess | null, cam: MicAccess | null) => {
      if (mic) micAccessRef.current = mic
      if (cam) camAccessRef.current = cam
      if (!room) return
      const attributes: Record<string, string> = { [ACCESS_AT_ATTRIBUTE]: String(Date.now()) }
      if (mic) attributes[MIC_ACCESS_ATTRIBUTE] = mic
      if (cam) attributes[CAM_ACCESS_ATTRIBUTE] = cam
      room.localParticipant.setAttributes(attributes).catch(() => undefined)
    },
    [room],
  )

  return {
    floating,
    celebrating,
    sendReaction,
    celebrate,
    hands,
    handQueue,
    myHandRaised,
    toggleHand,
    lowerHand,
    askToUnmute,
    unmuteRequest,
    dismissUnmuteRequest,
    askStudentMicHelp,
    micHelpRequest,
    dismissMicHelpRequest,
    askToShare,
    shareRequest,
    answerShare,
    holdShareTimeout,
    dismissShareRequest,
    sharePending,
    clearSharePending,
    canShare,
    shareStoppedAt,
    micAccess,
    camAccess,
    publishDeviceAccess,
  }
}
